import { useState } from 'react';

type Platform = 'macos' | 'linux' | 'windows';

export interface TokenError {
	error: string;
	message: string;
	license?: {
		agent_limit: number;
		current_agents: number;
		license_type: string;
	};
}

interface PlatformInstallGuideProps {
	registrationToken: string | null;
	tokenLoading: boolean;
	/** Compact mode for inline usage (e.g., Machines page) */
	compact?: boolean;
	/** License/limit error from the token API (403) */
	tokenError?: TokenError | null;
}

const PLATFORMS: { id: Platform; label: string; icon: JSX.Element }[] = [
	{
		id: 'macos',
		label: 'macOS',
		icon: (
			<svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
				<path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
			</svg>
		),
	},
	{
		id: 'linux',
		label: 'Linux',
		icon: (
			<svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
				<path d="M12.504 0c-.155 0-.315.008-.48.021-4.226.333-3.105 4.807-3.17 6.298-.076 1.092-.3 1.953-1.05 3.02-.885 1.051-2.127 2.75-2.716 4.521-.278.832-.41 1.684-.287 2.489a.424.424 0 00-.11.135c-.26.268-.45.6-.663.839-.199.199-.485.267-.797.4-.313.136-.658.269-.864.68-.09.189-.136.394-.132.602 0 .199.027.4.055.536.058.399.116.728.04.97-.249.68-.28 1.145-.106 1.484.174.334.535.47.94.601.81.2 1.91.135 2.774.6.926.466 1.866.67 2.616.47.526-.116.97-.464 1.208-.946.587-.003 1.23-.269 2.26-.334.699-.058 1.574.267 2.577.2.025.134.063.198.114.333l.003.003c.391.778 1.113 1.368 1.884 1.43.868.065 1.66-.529 1.735-1.481.047-.596-.24-1.075-.56-1.492-.325-.427-.662-.849-.727-1.339-.062-.476.109-.801.234-1.298a8.548 8.548 0 00.241-2.183c-.006-.14-.032-.27-.046-.399-.22-1.17-.588-2.146-1.29-2.98a5.097 5.097 0 00-1.097-1.093 4.876 4.876 0 00-.247-3.455A3.456 3.456 0 0012.504 0zm-.126 2.646a1.866 1.866 0 011.259.482c.354.273.563.685.636 1.15a3.026 3.026 0 00-.608-.024c-.366.025-.763.13-1.14.36a.456.456 0 01-.066-.173c-.041-.251-.155-.46-.268-.66-.097-.18-.183-.353-.19-.534a2.126 2.126 0 01.377-.601z" />
			</svg>
		),
	},
	{
		id: 'windows',
		label: 'Windows',
		icon: (
			<svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
				<path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801" />
			</svg>
		),
	},
];

// getInstallCommand returns the one-shot install command for the given
// platform, with the registration token embedded as an env var (sh) or
// flag (PowerShell). Single line, copy-paste, runs end-to-end:
//   download binary → register → install service → start.
function getInstallCommand(platform: Platform, token: string): string {
	switch (platform) {
		case 'macos':
		case 'linux':
			return `curl -sSL https://download.clawdgate.com/install.sh | CLAWDGATE_TOKEN=${token} sh`;
		case 'windows':
			return `$env:CLAWDGATE_TOKEN="${token}"; irm https://download.clawdgate.com/install.ps1 | iex`;
	}
}

function getInstallNote(platform: Platform): JSX.Element | null {
	switch (platform) {
		case 'macos':
		case 'linux':
			return (
				<p className="text-xs text-slate-500 dark:text-slate-400 mt-3">
					Installs the connector to <code className="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">~/.local/bin</code>,
					registers it with your account, installs a user-level service (LaunchAgent on macOS, systemctl --user on Linux), and starts it.
					No sudo, no further commands.
				</p>
			);
		case 'windows':
			return (
				<p className="text-xs text-slate-500 dark:text-slate-400 mt-3">
					Run in <strong>PowerShell as Administrator</strong>. Alternatively, use{' '}
					<a href="https://learn.microsoft.com/en-us/windows/wsl/" target="_blank" rel="noopener noreferrer" className="text-primary-600 dark:text-primary-400 underline">WSL</a>{' '}
					and follow the Linux instructions.
				</p>
			);
	}
}

function HowItWorks() {
	return (
		<div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl p-5 mb-6">
			<h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">How Clawdgate works</h4>
			<div className="flex items-center gap-3 text-sm text-slate-600 dark:text-slate-400">
				{/* AI Agent */}
				<div className="flex flex-col items-center text-center min-w-[80px]">
					<div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center mb-1.5">
						<svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
						</svg>
					</div>
					<span className="text-xs font-medium">AI Agent</span>
					<span className="text-[10px] text-slate-400 dark:text-slate-500">OpenClaw, etc.</span>
				</div>

				{/* Arrow */}
				<div className="flex flex-col items-center">
					<svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
					</svg>
				</div>

				{/* Clawdgate Agent */}
				<div className="flex flex-col items-center text-center min-w-[80px]">
					<div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-1.5">
						<img src="/img/logo.png" alt="" className="w-6 h-6 rounded" />
					</div>
					<span className="text-xs font-medium">Clawdgate</span>
					<span className="text-[10px] text-slate-400 dark:text-slate-500">Connector</span>
				</div>

				{/* Arrow */}
				<div className="flex flex-col items-center">
					<svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
					</svg>
				</div>

				{/* Dashboard */}
				<div className="flex flex-col items-center text-center min-w-[80px]">
					<div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mb-1.5">
						<svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
						</svg>
					</div>
					<span className="text-xs font-medium">You</span>
					<span className="text-[10px] text-slate-400 dark:text-slate-500">This dashboard</span>
				</div>
			</div>
			<p className="text-xs text-slate-500 dark:text-slate-400 mt-3">
				Clawdgate sits between your AI agents and the outside world. It monitors prompts for injection attacks, enforces security policies, and gives you full visibility and control — all from this dashboard.
			</p>
		</div>
	);
}

function CopyButton({ text }: { text: string }) {
	const [copied, setCopied] = useState(false);

	const copy = () => {
		navigator.clipboard.writeText(text);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	return (
		<button
			onClick={copy}
			className="absolute top-3 right-3 px-3 py-1.5 text-xs bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors cursor-pointer shadow-md"
		>
			{copied ? 'Copied!' : 'Copy'}
		</button>
	);
}

function PlatformTabs({ selected, onSelect }: { selected: Platform; onSelect: (p: Platform) => void }) {
	return (
		<div className="flex bg-slate-100 dark:bg-slate-800 rounded-xl p-1 mb-4">
			{PLATFORMS.map((p) => (
				<button
					key={p.id}
					onClick={() => onSelect(p.id)}
					className={`flex items-center gap-2 flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer ${
						selected === p.id
							? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
							: 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
					}`}
				>
					<span className={selected === p.id ? 'text-primary-600 dark:text-primary-400' : ''}>{p.icon}</span>
					{p.label}
				</button>
			))}
		</div>
	);
}

export default function PlatformInstallGuide({ registrationToken, tokenLoading, compact, tokenError }: PlatformInstallGuideProps) {
	const [platform, setPlatform] = useState<Platform>('macos');

	// v1.3.1+ one-shot install: token is embedded in the command, no
	// further interactive steps. installCmd is "" until the token loads
	// (we render a loading state instead of an unusable command).
	const installCmd = registrationToken
		? getInstallCommand(platform, registrationToken)
		: '';
	const exe = platform === 'windows' ? 'clawdgate-connector.exe' : 'clawdgate-connector';
	const sudo = ''; // never used now; retained for backwards compat with management-commands block
	const upgradeCmd = platform === 'windows'
		? `$env:CLAWDGATE_TOKEN="${registrationToken || '<your-token>'}"; irm https://download.clawdgate.com/install.ps1 | iex`
		: `curl -sSL https://download.clawdgate.com/install.sh | CLAWDGATE_TOKEN=${registrationToken || '<your-token>'} sh`;

	// Show license/limit error instead of install instructions
	if (tokenError) {
		const isLimitError = tokenError.error === 'Agent limit reached';
		return (
			<div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-6">
				<div className="flex items-start gap-3">
					<svg className="w-6 h-6 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
					</svg>
					<div>
						<p className="font-semibold text-amber-900 dark:text-amber-300 mb-1">
							{isLimitError ? 'Agent Limit Reached' : 'Subscription Required'}
						</p>
						<p className="text-sm text-amber-800 dark:text-amber-400 mb-3">{tokenError.message}</p>
						{tokenError.license && (
							<p className="text-xs text-amber-700 dark:text-amber-500 mb-3">
								Current usage: {tokenError.license.current_agents}/{tokenError.license.agent_limit} agents ({tokenError.license.license_type})
							</p>
						)}
						<a
							href="/billing"
							className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-lg transition-colors"
						>
							<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
							</svg>
							{isLimitError ? 'Upgrade Plan' : 'Subscribe Now'}
						</a>
					</div>
				</div>
			</div>
		);
	}

	if (compact) {
		return (
			<div className="space-y-4">
				<HowItWorks />
				<PlatformTabs selected={platform} onSelect={setPlatform} />

				{/* ONE command — copy, paste in terminal, done. */}
				<div>
					<p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
						Run this one command on the machine you want to connect:
					</p>
					{tokenLoading ? (
						<div className="bg-slate-900 dark:bg-black text-slate-400 p-4 rounded-xl font-mono text-sm">
							Loading your registration token…
						</div>
					) : !registrationToken ? (
						<div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-400 p-4 rounded-xl text-sm">
							Could not load registration token. Refresh the page or check the error above.
						</div>
					) : (
						<div className="relative">
							<pre className="bg-slate-900 dark:bg-black text-slate-100 p-4 rounded-xl font-mono text-xs overflow-x-auto pr-20">
								{installCmd}
							</pre>
							<CopyButton text={installCmd} />
						</div>
					)}
					{getInstallNote(platform)}
				</div>

				<div className="text-xs text-slate-500 dark:text-slate-400">
					<p>
						The command downloads the connector, registers it with your account using the embedded token, installs a user-level service, and starts it. No further steps, no sudo prompts. Token is good for one device.
					</p>
				</div>

				{/* Management commands */}
				<div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
					<p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Useful Commands</p>
					<div className="space-y-1.5 font-mono text-xs text-slate-600 dark:text-slate-400">
						<div className="flex gap-3"><code className="text-slate-100 bg-slate-800 px-2 py-0.5 rounded whitespace-nowrap">{sudo}{exe} start</code><span>Start the connector</span></div>
						<div className="flex gap-3"><code className="text-slate-100 bg-slate-800 px-2 py-0.5 rounded whitespace-nowrap">{sudo}{exe} stop</code><span>Stop the agent</span></div>
						<div className="flex gap-3"><code className="text-slate-100 bg-slate-800 px-2 py-0.5 rounded whitespace-nowrap">{sudo}{exe} status</code><span>Check agent status</span></div>
						<div className="flex gap-3"><code className="text-slate-100 bg-slate-800 px-2 py-0.5 rounded whitespace-nowrap">{sudo}{exe} reset</code><span>Reset registration</span></div>
						<div className="flex gap-3"><code className="text-slate-100 bg-slate-800 px-2 py-0.5 rounded whitespace-nowrap">{sudo}{exe} run</code><span>Run in foreground (debug)</span></div>
						{platform !== 'windows' && (
							<div className="flex gap-3 pt-1 border-t border-slate-200 dark:border-slate-700 mt-1">
								<code className="text-slate-100 bg-slate-800 px-2 py-0.5 rounded whitespace-nowrap">{platform === 'macos' ? "log stream --predicate 'senderImagePath contains \"clawdgate-connector\"' --level info" : 'journalctl --user -u clawdgate-connector -f'}</code>
								<span>View logs</span>
							</div>
						)}
					</div>
				</div>

				{/* Upgrade */}
				<div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
					<p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Manual Upgrade</p>
					<p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Re-run the install script to upgrade to the latest version:</p>
					<div className="relative">
						<pre className="bg-slate-900 dark:bg-black text-slate-100 p-3 rounded-lg font-mono text-xs overflow-x-auto">
							{upgradeCmd}
						</pre>
						<CopyButton text={upgradeCmd} />
					</div>
				</div>
			</div>
		);
	}

	// Full mode (Dashboard + Instances pages)
	return (
		<div className="space-y-6">
			<HowItWorks />
			<PlatformTabs selected={platform} onSelect={setPlatform} />

			{/* ONE STEP — install + register + start in a single command */}
			<div className="relative pl-8 pb-8 border-l-2 border-primary-200 dark:border-primary-900">
				<div className="absolute -left-3 top-0 w-6 h-6 bg-gradient-to-br from-primary-500 to-secondary-600 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-md">1</div>
				<h3 className="font-semibold text-slate-900 dark:text-white mb-2">Install the Connector</h3>
				<p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
					{platform === 'windows'
						? 'Run this command in PowerShell as Administrator. Your registration token is already embedded.'
						: 'Run this one command on the machine you want to connect. Your registration token is already embedded.'}
				</p>
				{tokenLoading ? (
					<div className="bg-slate-900 dark:bg-black text-slate-400 p-4 rounded-xl font-mono text-sm">
						Loading your registration token…
					</div>
				) : !registrationToken ? (
					<div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-400 p-4 rounded-xl text-sm">
						Could not load registration token. Refresh the page.
					</div>
				) : (
					<div className="relative">
						<pre className="bg-slate-900 dark:bg-black text-slate-100 p-4 rounded-xl overflow-x-auto font-mono text-xs pr-20">
							<code>{installCmd}</code>
						</pre>
						<CopyButton text={installCmd} />
					</div>
				)}
				{getInstallNote(platform)}
				<p className="text-xs text-slate-500 dark:text-slate-400 mt-3">
					The command downloads the connector, registers it with your account, installs a user-level service, and starts it. No further commands, no sudo prompts. Token is single-use — get a fresh one for each new device.
				</p>
				{platform !== 'windows' && (
					<details className="mt-3">
						<summary className="text-xs text-slate-500 dark:text-slate-400 cursor-pointer hover:text-slate-700 dark:hover:text-slate-300">Prefer to run the steps manually?</summary>
						<div className="mt-2 ml-3 text-xs text-slate-500 dark:text-slate-400 space-y-1">
							<div>1. Download: <code className="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded font-mono">curl -sSL https://download.clawdgate.com/install.sh | sh</code> (no token — will exit; you can also use <code className="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded font-mono">CLAWDGATE_TOKEN=… sh</code> to do it all in one)</div>
							<div>2. Register: <code className="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded font-mono">~/.local/bin/{exe} register {tokenLoading ? '<token>' : (registrationToken || '<token>')}</code></div>
							<div>3. Install service: <code className="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded font-mono">~/.local/bin/{exe} install</code></div>
							<div>4. Start: <code className="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded font-mono">~/.local/bin/{exe} start</code></div>
						</div>
					</details>
				)}
			</div>

			{/* Channel Configuration Recommendation */}
			{/* Management commands */}
			<div className="relative pl-8 pb-8 border-l-2 border-primary-200 dark:border-primary-900">
				<div className="absolute -left-3 top-0 w-6 h-6 bg-gradient-to-br from-primary-500 to-secondary-600 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-md">
					<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
					</svg>
				</div>
				<h3 className="font-semibold text-slate-900 dark:text-white mb-3">Useful Commands</h3>
				<div className="bg-slate-900 dark:bg-black rounded-xl p-4 font-mono text-sm space-y-2">
					<div className="flex items-start gap-4">
						<code className="text-green-400 whitespace-nowrap">{sudo}{exe} start</code>
						<span className="text-slate-500"># Start the connector</span>
					</div>
					<div className="flex items-start gap-4">
						<code className="text-green-400 whitespace-nowrap">{sudo}{exe} stop</code>
						<span className="text-slate-500"># Stop the agent</span>
					</div>
					<div className="flex items-start gap-4">
						<code className="text-green-400 whitespace-nowrap">{sudo}{exe} status</code>
						<span className="text-slate-500"># Check agent status</span>
					</div>
					<div className="flex items-start gap-4">
						<code className="text-green-400 whitespace-nowrap">{sudo}{exe} reset</code>
						<span className="text-slate-500"># Reset registration</span>
					</div>
					<div className="flex items-start gap-4">
						<code className="text-green-400 whitespace-nowrap">{sudo}{exe} run</code>
						<span className="text-slate-500"># Run in foreground (debug)</span>
					</div>
					{platform !== 'windows' && (
						<div className="flex items-start gap-4 pt-2 border-t border-slate-700">
							<code className="text-yellow-400 whitespace-nowrap">{platform === 'macos' ? "log stream --predicate 'senderImagePath contains \"clawdgate-connector\"' --level info" : 'journalctl --user -u clawdgate-connector -f'}</code>
							<span className="text-slate-500"># View logs</span>
						</div>
					)}
				</div>
			</div>

			{/* Upgrade */}
			<div className="relative pl-8">
				<div className="absolute -left-3 top-0 w-6 h-6 bg-gradient-to-br from-slate-400 to-slate-500 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-md">
					<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
					</svg>
				</div>
				<h3 className="font-semibold text-slate-900 dark:text-white mb-2">Upgrading</h3>
				<p className="text-sm text-slate-600 dark:text-slate-400 mb-3">Re-run the install script to upgrade to the latest version:</p>
				<div className="relative">
					<pre className="bg-slate-900 dark:bg-black text-slate-100 p-4 rounded-xl font-mono text-sm overflow-x-auto">
						<code>{upgradeCmd}</code>
					</pre>
					<CopyButton text={upgradeCmd} />
				</div>
			</div>
		</div>
	);
}
